import { apiFetch } from '@/lib/api';
import { loadAssistantChatState } from '@/lib/assistantChatPersistence';
import type { HistoryMessageLike } from '@/lib/geoChatBridge';

export type SearchResumeSummary = {
    sessionId: string | null;
    title: string;
    source: 'session' | 'history';
};

export type SearchResumeNavigationState =
    | { searchResumeAction: 'continue'; sessionId?: string | null }
    | { searchResumeAction: 'fresh' };

const FALLBACK_TITLE = 'Recent search';

export function titleFromSearchText(text: string | undefined | null): string {
    const trimmed = (text || '').trim();
    if (!trimmed) return FALLBACK_TITLE;
    return trimmed.length > 60 ? `${trimmed.slice(0, 60)}…` : trimmed;
}

type HistoryRow = {
    session_id?: string;
    preview?: string;
    messages?: Array<{ role?: string; content?: string }>;
};

function titleFromHistorySession(session: HistoryRow): string {
    const firstUser = session.messages?.find(m => m.role === 'user');
    return titleFromSearchText(firstUser?.content || session.preview);
}

export async function findResumableSearch(userId?: string | null): Promise<SearchResumeSummary | null> {
    const saved = loadAssistantChatState(userId);
    if (saved) {
        const firstUser = saved.messages.find(m => m.role === 'user');
        return {
            sessionId: saved.sessionId,
            title: titleFromSearchText(firstUser?.content),
            source: 'session',
        };
    }

    try {
        const response = await apiFetch('/chat/conversation-history?flow=search&limit=1');
        if (!response.ok) return null;
        const data = (await response.json()) as { sessions?: HistoryRow[] };
        const session = data.sessions?.[0];
        if (!session?.session_id) return null;
        return {
            sessionId: session.session_id,
            title: titleFromHistorySession(session),
            source: 'history',
        };
    } catch {
        return null;
    }
}

/** Flatten GET /chat/conversation/{id} rows into history-message shape for restore. */
export function conversationRowsToHistoryMessages(
    messages: Array<{
        role?: string;
        content?: string;
        meta_data?: Record<string, unknown> | null;
        properties?: HistoryMessageLike['properties'];
        search_filters?: HistoryMessageLike['search_filters'];
        geo_anchor?: HistoryMessageLike['geo_anchor'];
        map_viewport?: HistoryMessageLike['map_viewport'];
        suggested_replies?: HistoryMessageLike['suggested_replies'];
    }>,
): HistoryMessageLike[] {
    return messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => {
            const meta = m.meta_data && typeof m.meta_data === 'object' ? m.meta_data : {};
            return {
                role: m.role as string,
                content: m.content || '',
                properties:
                    m.properties ??
                    (meta.properties as HistoryMessageLike['properties']),
                search_filters:
                    m.search_filters ??
                    ((meta.collected_data as HistoryMessageLike['search_filters']) || null),
                geo_anchor:
                    m.geo_anchor ?? (meta.geo_anchor as HistoryMessageLike['geo_anchor']),
                map_viewport:
                    m.map_viewport ?? (meta.map_viewport as HistoryMessageLike['map_viewport']),
                suggested_replies:
                    m.suggested_replies ??
                    (meta.suggested_replies as HistoryMessageLike['suggested_replies']),
            };
        });
}
